import { StatCard } from '@/components/custom/StatCard';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { API_BASE_URL } from '@/config';
import { useAgentsApi } from '@/hooks/useAgentsApi';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { Building2, DollarSign, Download, Loader2, ShoppingCart, TrendingUp } from 'lucide-react';
import { useState } from 'react';

const typeLabels: Record<string, string> = {
  equipment_sale: 'Equipment Sale',
  affiliate_sub: 'Affiliate Subscription',
  individual_sub: 'Individual Subscription',
};

const typeIcons: Record<string, typeof DollarSign> = {
  equipment_sale: ShoppingCart,
  affiliate_sub: Building2,
  individual_sub: DollarSign,
};

const typeColors: Record<string, string> = {
  equipment_sale: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  affiliate_sub: 'bg-green-500/10 text-green-500 border-green-500/20',
  individual_sub: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
};

export default function AgentCommission() {
  const agentsApi = useAgentsApi();
  const { getToken } = useAuth();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const { data: summaryData } = useQuery({
    queryKey: ['agent-commission-summary'],
    queryFn: async () => {
      const response = await agentsApi.getCommissionSummary();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agent-commission', startDate, endDate],
    queryFn: async () => {
      const response = await agentsApi.getCommissionLedger({
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleFilter = () => {
    refetch();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/agents/commission/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commission-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
          <p className="text-destructive">Failed to load commission data</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const totals = data?.totals;

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">Commission Ledger</h1>
        <p className="mt-1 text-muted-foreground">
          Track your earnings from affiliate subscriptions and equipment sales.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatCard
          title="Total Commission"
          value={formatCurrency(totals?.total || 0)}
          subtitle="All time"
          icon={DollarSign}
          delay={0}
        />
        <StatCard
          title="Equipment Sales"
          value={formatCurrency(totals?.by_type?.equipment_sale || 0)}
          subtitle="From Shopify orders"
          icon={ShoppingCart}
          delay={50}
        />
        <StatCard
          title="Affiliate Subscriptions"
          value={formatCurrency(totals?.by_type?.affiliate_sub || 0)}
          subtitle="Monthly recurring"
          icon={Building2}
          delay={100}
        />
        <StatCard
          title="Individual Subscriptions"
          value={formatCurrency(totals?.by_type?.individual_sub || 0)}
          subtitle="Individual users"
          icon={TrendingUp}
          delay={150}
        />
      </div>

      {/* Date Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="flex-1 space-y-2">
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleFilter}>Filter</Button>
              {(startDate || endDate) && (
                <Button variant="outline" onClick={handleClearFilters}>
                  Clear
                </Button>
              )}
              <Button variant="outline" onClick={handleExportCSV} disabled={isExporting}>
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Summary */}
      {summaryData?.months && summaryData.months.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Monthly Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Equipment</TableHead>
                  <TableHead className="text-right">Affiliate Subs</TableHead>
                  <TableHead className="text-right">Individual Subs</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summaryData.months.map((month) => (
                  <TableRow key={month.period}>
                    <TableCell className="font-medium">{month.period}</TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatCurrency(month.equipment)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatCurrency(month.affiliate_subs)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatCurrency(month.individual_subs)}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-primary">
                      {formatCurrency(month.total)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 border-primary/20">
                  <TableCell className="font-bold">Grand Total</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell className="text-right font-bold text-primary">
                    {formatCurrency(summaryData.grand_total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Ledger Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" />
            Transaction History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data?.entries || data.entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <DollarSign className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No commission entries yet</h3>
              <p className="text-muted-foreground max-w-md">
                {startDate || endDate
                  ? 'No entries match your date range.'
                  : 'Your commission will appear here once affiliates subscribe or equipment orders are placed.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Commission</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.entries.map((entry, index) => {
                  const Icon = typeIcons[entry.type] || DollarSign;
                  return (
                    <TableRow
                      key={entry.id}
                      className="animate-fade-in"
                      style={{ animationDelay: `${index * 30}ms` }}
                    >
                      <TableCell className="text-muted-foreground">
                        {formatDate(entry.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={typeColors[entry.type] || ''}>
                          <Icon className="h-3 w-3 mr-1" />
                          {typeLabels[entry.type] || entry.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium max-w-[200px] truncate">
                        {entry.description}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{entry.period_month}</TableCell>
                      <TableCell className="text-right font-semibold text-primary">
                        {formatCurrency(entry.amount_cents)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
